// ============================================================================
// WalkIn API Service
// Vervangt alle localStorage/sessionStorage calls met echte backend calls.
// Backend: NestJS + PostgreSQL op https://api.joinwalkin.com
// ============================================================================

import { geocodeOne, calculateDistance } from './locationService';
import { normalizeCategory } from '../constants/categories';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.joinwalkin.com/api';

// ── Token helpers ──────────────────────────────────────────────────────────
// JWT token wordt opgeslagen in sessionStorage (niet localStorage) zodat
// elke browser tab/sessie zijn eigen login heeft, net als de huidige opzet.

export function getToken() {
  return sessionStorage.getItem('walkin_auth_token') || null;
}

export function setToken(token) {
  if (token) sessionStorage.setItem('walkin_auth_token', token);
  else sessionStorage.removeItem('walkin_auth_token');
}

export function getCurrentUser() {
  try {
    return JSON.parse(sessionStorage.getItem('walkin_auth_user') || 'null');
  } catch {
    return null;
  }
}

export function setCurrentUser(user) {
  if (user) sessionStorage.setItem('walkin_auth_user', JSON.stringify(user));
  else sessionStorage.removeItem('walkin_auth_user');
}

export function logout() {
  setToken(null);
  setCurrentUser(null);
}

// ── Generieke fetch wrapper ────────────────────────────────────────────────

// `authToken` overrides the stored session token for a single call. Used when
// an admin creates a retailer: the new retailer's own token is needed to write
// its profile, but the admin's session must stay untouched.
async function apiFetch(path, options = {}) {
  const { authToken, ...fetchOptions } = options;
  const token = authToken !== undefined ? authToken : getToken();
  const headers = {
    'Content-Type': 'application/json',
    ...(options.headers || {}),
  };
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(`${API_URL}${path}`, { ...fetchOptions, headers });

  if (!res.ok) {
    let message = `API error ${res.status}`;
    try {
      const data = await res.json();
      message = Array.isArray(data.message) ? data.message.join(', ') : (data.message || message);
    } catch { /* ignore parse error */ }
    const err = new Error(message);
    err.status = res.status;
    throw err;
  }

  // 204 No Content
  if (res.status === 204) return null;
  return res.json();
}

// ============================================================================
// AUTH — login/register voor retailer + admin
// ============================================================================

export async function login(email, password) {
  const data = await apiFetch('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });
  setToken(data.access_token);
  setCurrentUser(data.retailer || data.user);
  return data;
}

// Creates a retailer account in the backend WITHOUT touching the current
// session, so it is safe to call from the admin panel (admin stays logged in)
// and from the public signup page (which sends the user to /shop/login after).
//
// POST /auth/register only persists name, email, password, areaId and address.
// `description` has to be written in a second call to PUT /retailers/profile,
// authenticated as the new retailer.
//
// Coordinates: `PUT /retailers/profile` stores lat/lng since 2026-09-06, so a
// new shop gets its own location straight away — geocoded from the address it
// just typed when the caller has no coordinates of its own. A shop without
// coordinates has its deals fall back to the centre of its gebied, which put
// every shop in a winkelcentrum on one point of the map.
//
// `instructions` and `openingHours` were accepted and silently dropped until
// 2026-09-06; PUT /retailers/profile stores them now, so what a retailer
// types reaches the walker's deal page. POST /auth/register still ignores
// them, which is why they go in the profile patch below.
// A shop's own coordinates: the ones the caller already holds, or failing
// that the address geocoded. Returns null when neither yields a point, so the
// caller leaves the location unset instead of inventing one.
export async function resolveShopCoords({ lat, lng, address } = {}) {
  const given = toCoords(lat, lng);
  if (given) return given;
  if (!address || !address.trim()) return null;
  try {
    const hit = await geocodeOne(address);
    return hit ? toCoords(hit.lat, hit.lng) : null;
  } catch {
    // Nominatim is a third party and this runs while an account is being
    // created — never let it fail the account.
    return null;
  }
}

export async function createRetailer(input = {}) {
  const registerBody = {
    name: input.name,
    email: (input.email || '').toLowerCase().trim(),
    password: input.password,
  };
  // An unknown/invalid areaId makes the backend return a 500, so only send it
  // when it resolved to a real area.
  if (input.areaId) registerBody.areaId = input.areaId;
  if (input.address) registerBody.address = input.address;

  const data = await apiFetch('/auth/register', {
    method: 'POST',
    body: JSON.stringify(registerBody),
    authToken: null, // public endpoint — never send the caller's token
  });

  let retailer = data.retailer || data.user || {};

  const profilePatch = {};
  if (input.description) profilePatch.description = input.description;
  if (input.openingHours) profilePatch.openingHours = input.openingHours;
  if (input.instructions) profilePatch.instructions = input.instructions;

  const coords = await resolveShopCoords(input);
  if (coords) {
    profilePatch.lat = coords.lat;
    profilePatch.lng = coords.lng;
  }

  if (Object.keys(profilePatch).length && data.access_token) {
    try {
      const updated = await apiFetch('/retailers/profile', {
        method: 'PUT',
        body: JSON.stringify(profilePatch),
        authToken: data.access_token,
      });
      if (updated && typeof updated === 'object') retailer = { ...retailer, ...updated };
    } catch (err) {
      // The account itself exists at this point; losing the optional profile
      // fields must not fail the whole creation.
      console.warn('[WalkIn] Retailer created, profile update failed:', err);
    }
  }

  return { ...data, retailer: normalizeRetailer(retailer) };
}

// Self-signup that also logs the new retailer in immediately.
export async function register(payload) {
  const data = await createRetailer(payload);
  setToken(data.access_token);
  setCurrentUser(data.retailer || data.user);
  return data;
}

export async function forgotPassword(email) {
  return apiFetch('/auth/forgot-password', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

export async function resetPassword(token, newPassword) {
  return apiFetch('/auth/reset-password', {
    method: 'POST',
    body: JSON.stringify({ token, newPassword }),
  });
}

// ============================================================================
// NORMALISATIE — backend velden ↔ frontend velden
// De prototype-componenten verwachten andere veldnamen dan de backend
// teruggeeft (bv. `expiresAt` vs `validUntil`, `shopName` vs `retailerName`,
// status 'actief' vs 'active'). Deze helpers vertalen heen en weer zodat de
// componenten hun bestaande veldnamen kunnen blijven gebruiken.
// ============================================================================

const STATUS_TO_NL = {
  active: 'actief',
  paused: 'gepauzeerd',
  scheduled: 'gepland',
  expired: 'verlopen',
  deleted: 'verwijderd',
};
const STATUS_TO_API = {
  actief: 'active',
  gepauzeerd: 'paused',
  gepland: 'scheduled',
  verlopen: 'expired',
  verwijderd: 'deleted',
};

export function mapStatusToNL(s) {
  return STATUS_TO_NL[s] || s;
}
export function mapStatusToApi(s) {
  return STATUS_TO_API[s] || s;
}

// De backend heeft geen job die een deal laat verlopen: `deals.status` bevat
// alleen 'active' of 'paused', dus een deal waarvan validUntil voorbij is komt
// nog steeds terug als 'active' — en een deal die volgende week begint ook.
// De winkelier-UI sorteert deals per status in tabs, dus een verlopen deal
// bleef voor altijd in "Actief" staan en zijn "Heractiveer"-knop verscheen
// nooit. Leid dat deel van de lifecycle hier af uit de datums in plaats van
// de kolom te vertrouwen. Alleen 'active' wordt heringevuld: een pauze die de
// winkelier zelf zette moet blijven staan, en `active: false` (wat
// DELETE /deals/:id zet zonder de status aan te raken) betekent verwijderd.
function deriveDealStatus(d) {
  if (d.active === false) return 'verwijderd';
  const stored = mapStatusToNL(d.status);
  if (stored !== 'actief') return stored;
  const now = Date.now();
  const until = d.validUntil ?? d.expiresAt;
  if (until && new Date(until).getTime() <= now) return 'verlopen';
  if (d.validFrom && new Date(d.validFrom).getTime() > now) return 'gepland';
  return 'actief';
}

// Backend deal → frontend deal (behoudt originele velden + voegt aliassen toe)
export function normalizeDeal(d) {
  if (!d || typeof d !== 'object') return d;
  return {
    ...d,
    expiresAt: d.validUntil ?? d.expiresAt ?? null,
    shopName: d.retailerName ?? d.shopName ?? '',
    status: deriveDealStatus(d),
    views: Number(d.totalViews ?? d.views ?? 0),
    redemptions: Number(d.totalRedemptions ?? d.redemptions ?? 0),
    lat: d.retailerLat ?? d.lat ?? null,
    lng: d.retailerLng ?? d.lng ?? null,
    instructions: d.retailerInstructions ?? d.instructions ?? null,
    // The deal rows carry areaId but (until the backend joins it in) no area
    // name or code — withAreaInfo() fills those from /areas.
    area: d.area ?? d.areaName ?? '',
    areaCode: d.areaCode ?? null,
    // One vocabulary everywhere: legacy/free-text values fold into the
    // canonical list so every deal sits under exactly one feed filter.
    category: d.category ? normalizeCategory(d.category) : d.category,
    // /deals/nearby returns distanceMeters as a string; expose it as a number
    // so callers can format it without re-parsing.
    distanceM: d.distanceMeters != null ? Number(d.distanceMeters) : null,
  };
}

// De prototype gebruikt korte NL/EN korting-types; de backend verwacht
// 'percentage' | 'amount' | 'free'.
const DISCOUNT_TYPE_MAP = {
  percent: 'percentage',
  percentage: 'percentage',
  amount: 'amount',
  bedrag: 'amount',
  fixed: 'amount',
  free: 'free',
  gratis: 'free',
};

// Frontend deal payload → backend deal payload (alleen bekende input velden).
// De backend-DTO vereist o.a. een numerieke discountValue en numerieke
// lat/lng, dus die worden hier afgedwongen.
export function denormalizeDeal(payload = {}) {
  const out = {};
  const passthrough = [
    'title', 'description', 'category', 'emoji',
    'discountLabel', 'imageUrl',
    'validFrom', 'validFromTime', 'validUntilTime', 'maxRedemptions',
    'lat', 'lng',
    // De soft-delete vlag. Zonder deze kon een verwijderde deal niet terug
    // worden gezet, want DELETE /deals/:id zet `active: false` en niets anders
    // zette hem ooit weer op true.
    'active',
    // POST /deals accepts and stores areaId, but it was never sent, so every
    // deal landed with areaId: null and nothing could be scoped by gebied.
    'areaId',
  ];
  for (const k of passthrough) {
    if (payload[k] !== undefined) out[k] = payload[k];
  }
  const validUntil = payload.validUntil ?? payload.expiresAt;
  if (validUntil !== undefined) out.validUntil = validUntil;
  if (payload.status !== undefined) out.status = mapStatusToApi(payload.status);
  if (payload.discountType !== undefined) {
    out.discountType = DISCOUNT_TYPE_MAP[payload.discountType] || payload.discountType;
  }
  if (payload.discountValue !== undefined) {
    const n = Number(payload.discountValue);
    out.discountValue = Number.isFinite(n) ? n : 0;
  }
  return out;
}

// Backend retailer → frontend shop
export function normalizeRetailer(r) {
  if (!r || typeof r !== 'object') return r;

  // Only the detail routes project the wallet — GET /retailers/admin/all does
  // not — so a missing counter stays undefined rather than defaulting to 0,
  // which would render as "no tokens" for a shop that was simply never asked
  // about. tokensAvailable is computed by the backend (purchased - used) and
  // is never sent back to it.
  const count = (v) => (v === null || v === undefined ? undefined : Number(v));

  const out = {
    ...r,
    shopName: r.name ?? r.shopName ?? '',
    area: r.areaName ?? r.area ?? '',
    status: r.active === false ? 'geblokkeerd' : 'actief',
    tokensPurchased: count(r.tokensPurchased),
    tokensUsed: count(r.tokensUsed),
    tokensAvailable: count(r.tokensAvailable),
  };

  // Plan / Unlimited, from the database (backend 0.3: token_packages +
  // retailer_subscriptions). Only present once that migration runs; without it
  // the fields stay undefined and the screens keep their old behaviour.
  if (r.isUnlimited !== undefined) {
    out.subscription = r.isUnlimited ? 'unlimited' : 'tokens';
    out.subscriptionFromDb = true;
    // The snake_case names are what the admin screens already render.
    out.subscription_plan = r.subscriptionPlan || (r.isUnlimited ? 'unlimited' : null);
    out.subscription_status = r.subscriptionStatus || null;
    out.subscription_start_date = r.subscriptionStartedAt || null;
    out.subscription_cancelled_at = r.subscriptionCancelledAt || null;
    out.subscription_end_date = r.subscriptionEndsAt || null;
    out.subscription_source = r.subscriptionSource || null;
    out.fallback_plan = r.fallbackPlan || null;
  }
  return out;
}

// ============================================================================
// TOKEN PACKAGES + UNLIMITED — backend 0.3 (tables token_packages,
// retailer_subscriptions, token_transactions, orders)
// ============================================================================

// The three packages as they were hardcoded up to 0.2. Only used when the
// backend does not serve /token-packages yet, so the shop screens keep working.
export const FALLBACK_PACKAGES = [
  { code: 'starter', name: 'Starter', type: 'tokens', tokens: 4, priceExCents: 1995, vatRate: 21, sortOrder: 3, active: true, description: 'Deal tokens Starter (4x)' },
  { code: 'growth', name: 'Growth', type: 'tokens', tokens: 15, priceExCents: 5995, vatRate: 21, sortOrder: 2, active: true, description: 'Deal tokens Growth (15x)' },
  { code: 'unlimited', name: 'Unlimited', type: 'subscription', tokens: null, durationMonths: 1, priceExCents: 9900, vatRate: 21, sortOrder: 1, active: true, description: 'Unlimited abonnement (1 maand)' },
];

const eur = (cents) => (Math.round(cents) / 100).toFixed(2).replace('.', ',');

// Backend package → the shape the shop/admin screens use (prices as Dutch
// strings, as the invoice code expects).
export function normalizePackage(p) {
  if (!p || typeof p !== 'object') return p;
  const priceExCents = Number(p.priceExCents ?? 0);
  const vatRate = Number(p.vatRate ?? 21);
  const vatCents = Math.round((priceExCents * vatRate) / 100);
  const isSub = p.type === 'subscription';
  const tokens = p.tokens == null ? null : Number(p.tokens);
  return {
    ...p,
    id: p.code,          // screens address packages by code (?package=growth)
    packageId: p.id,     // database id
    type: isSub ? 'subscription' : 'tokens',
    tokens: isSub ? '∞' : tokens,
    tokensLabel: isSub ? 'Onbeperkt' : String(tokens ?? 0),
    recurring: isSub,
    priceExCents,
    vatRate,
    vatCents,
    priceInclCents: priceExCents + vatCents,
    priceEx: eur(priceExCents),
    vat: eur(vatCents),
    priceIncl: eur(priceExCents + vatCents),
    active: p.active !== false,
    sortOrder: Number(p.sortOrder ?? 0),
    description: p.description || (isSub ? `${p.name} abonnement` : `Deal tokens ${p.name} (${tokens}x)`),
  };
}

const byOrder = (a, b) => a.sortOrder - b.sortOrder;

// Active packages for the shop. { packages, live } — live=false means the
// backend has no /token-packages yet and the 0.2 prices are shown.
export async function getTokenPackages() {
  try {
    const rows = await apiFetch('/token-packages');
    const list = (Array.isArray(rows) ? rows : []).map(normalizePackage).filter((p) => p.active);
    if (list.length) return { packages: list.sort(byOrder), live: true };
  } catch (err) {
    if (err?.status && err.status !== 404) console.warn('[WalkIn] token-packages:', err.message);
  }
  return { packages: FALLBACK_PACKAGES.map(normalizePackage).sort(byOrder), live: false };
}

// Admin: every package, including inactive ones.
export async function adminGetTokenPackages() {
  const rows = await apiFetch('/admin/token-packages');
  return (Array.isArray(rows) ? rows : []).map(normalizePackage).sort(byOrder);
}

// Admin: create (no packageId) or update a package. Prices go in cents.
export async function adminSaveTokenPackage(pkg) {
  const body = {
    code: pkg.code,
    name: pkg.name,
    type: pkg.type === 'subscription' ? 'subscription' : 'tokens',
    tokens: pkg.type === 'subscription' ? null : Number(pkg.tokens) || 0,
    durationMonths: pkg.type === 'subscription' ? Number(pkg.durationMonths) || 1 : null,
    priceExCents: Math.round(Number(pkg.priceExCents) || 0),
    vatRate: Number(pkg.vatRate ?? 21),
    description: pkg.description || null,
    active: pkg.active !== false,
    sortOrder: Number(pkg.sortOrder) || 0,
  };
  const saved = pkg.packageId
    ? await apiFetch(`/admin/token-packages/${pkg.packageId}`, { method: 'PUT', body: JSON.stringify(body) })
    : await apiFetch('/admin/token-packages', { method: 'POST', body: JSON.stringify(body) });
  return normalizePackage(saved);
}

// Retailer buys a package. The backend creates the order (invoice number),
// adds the tokens or starts Unlimited, and writes the ledger — all in one
// transaction. Payment is simulated in the pilot (paymentStatus 'paid').
// Returns { order, retailer } (retailer normalised, incl. wallet + plan).
export async function purchasePackage(code, paymentMethod) {
  const res = await apiFetch('/retailers/me/purchases', {
    method: 'POST',
    body: JSON.stringify({ packageCode: code, paymentMethod }),
  });
  return { order: res?.order || null, retailer: normalizeRetailer(res?.retailer || null) };
}

// Retailer cancels Unlimited; it stays active until the end of the month.
export async function cancelMySubscription() {
  const res = await apiFetch('/retailers/me/subscription/cancel', { method: 'POST' });
  return normalizeRetailer(res);
}

// Admin: put a shop on Unlimited (optionally until a date — e.g. the end of
// the pilot) or back on tokens.
export async function adminSetSubscription(retailerId, { plan, endsAt = null, source = 'admin', note } = {}) {
  const res = await apiFetch(`/admin/shops/${retailerId}/subscription`, {
    method: 'PUT',
    body: JSON.stringify({
      plan: plan === 'unlimited' ? 'unlimited' : 'tokens',
      endsAt: endsAt || null,
      source,
      note: note || undefined,
    }),
  });
  return normalizeRetailer(res);
}

// Token ledger. Without retailerId: the latest mutations platform-wide.
export async function getTokenTransactions({ retailerId, limit = 20 } = {}) {
  const q = new URLSearchParams({ limit: String(limit) });
  if (retailerId) q.set('retailerId', retailerId);
  const rows = await apiFetch(`/admin/token-transactions?${q}`);
  return Array.isArray(rows) ? rows : [];
}

// ============================================================================
// AREAS — winkelgebieden
// ============================================================================

export async function getAreas() {
  return apiFetch('/areas');
}

// Areas the walker side is allowed to see. /areas returns every area with an
// `active` flag; an area switched off in the admin panel must disappear from
// detection and from the home page.
export async function getActiveAreas() {
  const areas = await getAreas();
  if (!Array.isArray(areas)) return [];
  return areas.filter((a) => a.active !== false);
}

// Name initials, used as the gebiedscode for areas that have none stored yet.
export function deriveAreaCode(name) {
  return (name || '')
    .split(/\s+/)
    .map((w) => w[0])
    .filter(Boolean)
    .join('')
    .toUpperCase()
    .slice(0, 4) || 'GEN';
}

// Backend area → the shape the screens use.
//
// `code` (gebiedscode) and `address` (gebiedsadres) are the two columns added
// in backend migration 0.3 (see BACKEND_CHANGES_0.3.md). Until that migration
// runs the API does not return them; the code then falls back to the name
// initials and is flagged `codeStored: false` so the admin screen can say so.
export function normalizeArea(a) {
  if (!a || typeof a !== 'object') return a;
  const storedCode = (a.code || '').trim();
  const code = storedCode || (a.prefix || '').trim() || deriveAreaCode(a.name);
  return {
    ...a,
    lat: a.lat != null ? Number(a.lat) : a.lat,
    lng: a.lng != null ? Number(a.lng) : a.lng,
    radius: Number(a.radiusM ?? a.radius ?? DEFAULT_NEARBY_RADIUS_M),
    active: a.active !== false,
    status: a.active === false ? 'inactief' : 'actief',
    code,
    codeStored: !!storedCode,
    // Kept for the screens that still say "prefix".
    prefix: code,
    address: a.address || '',
    dealCount: Number(a.dealCount || 0),
    retailerCount: Number(a.retailerCount || 0),
  };
}

// ── Area lookup (id → area), cached briefly ─────────────────────────────────
// Deal rows carry only areaId. Every screen that shows a gebied name or code
// resolves it through this one cached /areas call instead of each keeping its
// own hardcoded list of area names.
const AREA_CACHE_MS = 60 * 1000;
let areaCache = null; // { at, promise }

export function getAreaLookup({ fresh = false } = {}) {
  const now = Date.now();
  if (!fresh && areaCache && now - areaCache.at < AREA_CACHE_MS) return areaCache.promise;
  const promise = getAreas()
    .then((rows) => new Map(
      (Array.isArray(rows) ? rows : []).map((a) => [a.id, normalizeArea(a)])
    ))
    .catch(() => {
      areaCache = null; // do not cache a failure
      return new Map();
    });
  areaCache = { at: now, promise };
  return promise;
}

export function clearAreaCache() {
  areaCache = null;
}

// Adds `area` (name) and `areaCode` to deals that only carry an areaId. A deal
// whose backend row already has an areaName keeps it.
export async function withAreaInfo(deals) {
  const list = Array.isArray(deals) ? deals : [deals];
  if (!list.some((d) => d && d.areaId && (!d.area || !d.areaCode))) {
    return Array.isArray(deals) ? deals : list[0];
  }
  const lookup = await getAreaLookup();
  const out = list.map((d) => {
    if (!d || !d.areaId) return d;
    const a = lookup.get(d.areaId);
    if (!a) return d;
    return { ...d, area: d.area || a.name, areaCode: d.areaCode || a.code };
  });
  return Array.isArray(deals) ? out : out[0];
}

// Is a point covered by any (active) gebied? A gebied is a centre point plus a
// radius, so "covered" is simply: distance to the centre <= radius. Returns the
// nearest covering area, or null when the address lies outside every gebied.
export function findCoveringArea(lat, lng, areas) {
  const la = Number(lat);
  const ln = Number(lng);
  if (!Number.isFinite(la) || !Number.isFinite(ln)) return null;
  let best = null;
  for (const raw of areas || []) {
    const a = raw && raw.radius !== undefined ? raw : normalizeArea(raw);
    if (!a || a.active === false) continue;
    const alat = Number(a.lat);
    const alng = Number(a.lng);
    if (!Number.isFinite(alat) || !Number.isFinite(alng)) continue;
    const d = calculateDistance(la, ln, alat, alng);
    if (d <= Number(a.radius) && (!best || d < best.distanceM)) {
      best = { area: a, distanceM: d };
    }
  }
  return best;
}

// Tells the WalkIn admins that a retailer entered a shop address outside every
// gebied, so they can create one. The backend sends the e-mail (Resend) — see
// POST /areas/coverage-requests in BACKEND_CHANGES_0.3.md. Returns
// { sent: boolean } and never throws: the retailer's save must not depend on it.
export async function requestAreaCoverage({ address, lat, lng, shopName, email } = {}) {
  try {
    await apiFetch('/areas/coverage-requests', {
      method: 'POST',
      body: JSON.stringify({
        address,
        lat: Number.isFinite(Number(lat)) ? Number(lat) : undefined,
        lng: Number.isFinite(Number(lng)) ? Number(lng) : undefined,
        shopName: shopName || undefined,
        email: email || undefined,
      }),
    });
    return { sent: true };
  } catch (err) {
    console.warn('[WalkIn] Coverage request not delivered:', err?.status, err?.message);
    return { sent: false, status: err?.status };
  }
}

export async function getAreasAdmin() {
  const areas = await getAreas();
  return Array.isArray(areas) ? areas.map(normalizeArea) : [];
}

// Gebiedscode + gebiedsadres, set by the admin when the gebied is created.
// Both are columns added by backend migration 0.3. The backend's
// ValidationPipe runs with whitelist (unknown fields are stripped, not
// rejected), so sending them to a backend without the migration is harmless —
// they are simply not stored yet.
function areaCodeAndAddress(input = {}) {
  const out = {};
  const code = (input.code ?? input.prefix);
  if (code !== undefined && String(code).trim()) {
    out.code = String(code).trim().toUpperCase().slice(0, 10);
  }
  if (input.address !== undefined) out.address = String(input.address || '').trim();
  return out;
}

// Admin edit of an area — status (active) and radius both live here.
// PUT /areas/:id is role-gated to walkin_admin.
// Only fields the backend actually stores are sent; `prefix`/`description`
// have no column and would be stripped anyway.
export async function updateArea(id, patch = {}) {
  const body = {};
  if (patch.name !== undefined) body.name = patch.name;
  if (patch.city !== undefined) body.city = patch.city;
  if (patch.lat !== undefined) body.lat = Number(patch.lat);
  if (patch.lng !== undefined) body.lng = Number(patch.lng);
  if (patch.radius !== undefined) body.radiusM = clampRadius(patch.radius);
  if (patch.radiusM !== undefined) body.radiusM = clampRadius(patch.radiusM);
  if (patch.active !== undefined) body.active = !!patch.active;
  else if (patch.status !== undefined) body.active = patch.status !== 'inactief';
  Object.assign(body, areaCodeAndAddress(patch));

  clearAreaCache();
  return normalizeArea(await apiFetch(`/areas/${id}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  }));
}

export async function setAreaActive(id, active) {
  return updateArea(id, { active: !!active });
}

// POST /areas and DELETE /areas/:id are both role-gated to walkin_admin.
export async function createArea(input = {}) {
  const body = {
    name: input.name,
    city: input.city,
    lat: Number(input.lat),
    lng: Number(input.lng),
    radiusM: clampRadius(input.radius ?? input.radiusM),
  };
  if (input.active !== undefined) body.active = !!input.active;
  else if (input.status !== undefined) body.active = input.status !== 'inactief';
  Object.assign(body, areaCodeAndAddress(input));

  clearAreaCache();
  return normalizeArea(await apiFetch('/areas', {
    method: 'POST',
    body: JSON.stringify(body),
  }));
}

export async function deleteArea(id) {
  clearAreaCache();
  return apiFetch(`/areas/${id}`, { method: 'DELETE' });
}

// Resolves a human-readable area name (what the forms show) to the backend
// area UUID that /auth/register expects. Returns null when it cannot be
// resolved, so the caller can register without an area rather than send a
// bogus id (which the backend answers with a 500).
export async function getAreaIdByName(name) {
  if (!name) return null;
  const wanted = name.trim().toLowerCase();
  try {
    const areas = await getAreas();
    if (!Array.isArray(areas)) return null;
    return areas.find((a) => (a.name || '').trim().toLowerCase() === wanted)?.id || null;
  } catch {
    return null;
  }
}

// ============================================================================
// DEALS — walker kant (publiek, geen auth nodig)
// ============================================================================

// The backend rejects anything above 10 km with
// "radius must not be greater than 10000", so requests are clamped rather
// than failing the whole call.
export const MAX_NEARBY_RADIUS_M = 10000;
export const DEFAULT_NEARBY_RADIUS_M = 10000;

export function clampRadius(radius) {
  const n = Number(radius);
  if (!Number.isFinite(n) || n <= 0) return DEFAULT_NEARBY_RADIUS_M;
  return Math.min(Math.round(n), MAX_NEARBY_RADIUS_M);
}

// `areaId` scopes the result to one gebied server-side, on the deal's own
// area_id. Without it the endpoint filters on distance alone.
export async function getNearbyDeals(
  lat, lng, radius = DEFAULT_NEARBY_RADIUS_M, { areaId } = {},
) {
  const r = clampRadius(radius);
  const scope = areaId ? `&areaId=${encodeURIComponent(areaId)}` : '';
  const deals = await apiFetch(`/deals/nearby?lat=${lat}&lng=${lng}&radius=${r}${scope}`);
  return Array.isArray(deals) ? deals.map(normalizeDeal) : [];
}

// Deals for the home page when the walker's location is not known yet. There
// is no "all deals" endpoint on the walker side and /deals/nearby is capped at
// 10 km, so this fans out over the active areas and merges the results.
// Ordered by validFrom descending — the nearby rows carry no createdAt, so
// that is the closest available proxy for "most recently added".
export async function getRecentDeals(limit = 3) {
  let areas;
  try {
    areas = await getActiveAreas();
  } catch {
    return [];
  }
  if (!Array.isArray(areas) || areas.length === 0) return [];

  const results = await Promise.all(
    areas.map((a) => getAreaDeals(a).catch(() => []))
  );

  const byId = new Map();
  for (const deal of results.flat()) {
    if (deal?.id && !byId.has(deal.id)) byId.set(deal.id, deal);
  }

  return [...byId.values()]
    .sort((a, b) => new Date(b.validFrom || 0) - new Date(a.validFrom || 0))
    .slice(0, limit);
}

// ── Deals per gebied ──────────────────────────────────────────────────────
// /deals/nearby filters purely on distance from a point and ignores areaId
// (verified against the live API), and the rows it returns carry no areaId at
// all, so a deal cannot be matched to its gebied client-side either.
//
// Meanwhile the seeded areas overlap heavily: Winkelcentrum Gelderlandplein is
// 633 m from Amsterdam Zuid AS and Amsterdamsepoort is 5.9 km away, while every
// one of them has radiusM = 10000. A 10 km sweep from any Amsterdam centroid
// therefore returned every Amsterdam deal — which is why deals showed up in
// every gebied.
//
// So a gebied is resolved by nearest centroid: query the area at its own
// radius, then drop only those deals that a neighbouring centroid is strictly
// closer to. That keeps the gebieden separate without shrinking anyone's
// search area, and stops being a workaround the moment /deals/nearby projects
// and filters on areaId.
// Coordinates arrive as numbers today, but Nominatim hands back strings and
// the admin form writes whatever the inputs hold, so every read is coerced.
function areaPoint(area) {
  const lat = Number(area?.lat);
  const lng = Number(area?.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { lat, lng };
}

function areaRadius(area) {
  return clampRadius(area?.radiusM ?? area?.radius);
}

// Deals belonging to one gebied.
//
// The gebied is the deal's own areaId — set at creation from the retailer's
// gebied — and /deals/nearby filters on it. Distance used to stand in for it:
// the area was queried at its full radius and a deal was dropped when a
// neighbouring centroid was strictly closer. That guess held only while every
// deal sat exactly on its area's centroid. A shop can be nearer a
// neighbouring winkelcentrum's centre than its own — the Beethovenstraat deal
// registered under Amsterdam Zuid AS is 1595 m from that centroid and 1213 m
// from Gelderlandplein's — and the heuristic then files it under the wrong
// gebied. The radius still applies, so a deal has to be both in the gebied
// and within its reach.
export async function getAreaDeals(area) {
  if (!area) return [];
  const here = areaPoint(area);
  if (!here) return [];
  return getNearbyDeals(here.lat, here.lng, areaRadius(area), { areaId: area.id });
}

// Distances anchored on the WALKER, keyed by deal id.
//
// /deals/nearby computes distanceMeters from whatever point the query used,
// and getAreaDeals queries from the gebied centroid — so the distances on an
// area-scoped list describe the centroid, not the walker. Worse, deals inherit
// the area centroid as their own coordinates (retailers have no lat/lng of
// their own), so a centroid-anchored query returns distanceMeters: 0 for every
// deal. That is where the home page's "0 m" came from.
//
// One extra walker-anchored query fixes it: the backend already knows how to
// measure, it just has to be asked from the right point. Deals further than
// the radius simply do not come back and are left without a distance.
export async function getWalkerDistanceMap(lat, lng) {
  const out = new Map();
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return out;
  let deals;
  try {
    deals = await getNearbyDeals(lat, lng, MAX_NEARBY_RADIUS_M);
  } catch {
    return out;
  }
  for (const deal of deals) {
    if (deal?.id && Number.isFinite(deal.distanceM)) out.set(deal.id, deal.distanceM);
  }
  return out;
}

// The gebied the walker picked in /detect, resolved against the backend list.
//
// The last resort is a loose name match. /areas only serves active rows, and
// the table holds near-duplicates of the same winkelcentrum: 'Winkelcentrum
// Amsterdamse Poort' (inactive, and the one walkers had already picked) next
// to an active 'Winkelcentrum amsterdamsepoort'. A walker holding the retired
// id matched neither on id nor on an exact name, so their gebied silently
// stopped resolving.
const normalizeAreaName = (s) => (s || '').toLowerCase().replace(/\s+/g, '');

export async function getSelectedArea() {
  const id = sessionStorage.getItem('walkin_area_id');
  const name = sessionStorage.getItem('walkin_area_name');
  if (!id && !name) return null;
  try {
    const areas = await getActiveAreas();
    const found =
      (id && areas.find((a) => a.id === id)) ||
      (name && areas.find((a) => a.name === name)) ||
      (name && areas.find((a) => normalizeAreaName(a.name) === normalizeAreaName(name))) ||
      null;
    if (!found) console.warn('[walkin] gebied niet herkend', { id, name });
    return found ? { area: found, areas } : null;
  } catch {
    return null;
  }
}

// The single entry point every consumer surface uses, so the home page, the
// feed, the map and the journey all scope deals the same way. Falls back to a
// plain radius query around the walker when no gebied is known.
export async function getDealsForSelectedArea() {
  const selected = await getSelectedArea();
  if (selected) {
    return getAreaDeals(selected.area);
  }

  const lat = parseFloat(sessionStorage.getItem('walkin_user_lat'));
  const lng = parseFloat(sessionStorage.getItem('walkin_user_lng'));
  if (Number.isNaN(lat) || Number.isNaN(lng)) return [];
  return getNearbyDeals(
    lat, lng, parseFloat(sessionStorage.getItem('walkin_area_radius'))
  );
}

export async function getDealById(id) {
  return normalizeDeal(await apiFetch(`/deals/${id}`));
}

// The deal detail page: the deal plus its gebied name/code resolved from
// /areas (the deal row itself only carries areaId).
export async function getDealDetail(id) {
  return withAreaInfo(await getDealById(id));
}

// A deal removed via DELETE /deals/:id (active = false). The row stays in the
// database for statistics and invoices, but it no longer exists for anyone
// using the app, so every list hides it.
export function isRemovedDeal(d) {
  return !!d && (d.active === false || d.status === 'verwijderd');
}

// ============================================================================
// DEALS — retailer kant (auth vereist)
// ============================================================================

export async function getMyDeals() {
  const deals = await apiFetch('/deals/retailer/my-deals');
  return Array.isArray(deals) ? deals.map(normalizeDeal) : [];
}

export async function createDeal(payload) {
  return normalizeDeal(await apiFetch('/deals', {
    method: 'POST',
    body: JSON.stringify(denormalizeDeal(payload)),
  }));
}

export async function updateDeal(id, payload) {
  return normalizeDeal(await apiFetch(`/deals/${id}`, {
    method: 'PUT',
    body: JSON.stringify(denormalizeDeal(payload)),
  }));
}

export async function updateDealStatus(id, status) {
  return apiFetch(`/deals/${id}/status`, {
    method: 'PATCH',
    body: JSON.stringify({ status: mapStatusToApi(status) }),
  });
}

export async function deleteDeal(id) {
  return apiFetch(`/deals/${id}`, { method: 'DELETE' });
}

// Every deal on the platform, whatever its status — the admin deals overview.
// GET /deals/all is role-gated to walkin_admin.
export async function getAllDealsAdmin() {
  const deals = await apiFetch('/deals/all');
  return Array.isArray(deals) ? deals.map(normalizeDeal) : [];
}

// ============================================================================
// REDEMPTIONS — QR claimen, scannen, inwisselen
// ============================================================================

// A device must have a walker session on the backend before it can claim a QR
// code, otherwise generate-qr answers 400 "Walker sessie niet gevonden".
// POST /walkers/register is idempotent (the same deviceId returns the same
// walker), so this can be called freely; the flag just avoids the round trip.
let walkerSessionReady = false;

export async function ensureWalkerSession(deviceId = getOrCreateDeviceId()) {
  if (walkerSessionReady) return true;
  try {
    await registerWalker(deviceId);
    walkerSessionReady = true;
    return true;
  } catch (err) {
    console.warn('[WalkIn] Walker registration failed:', err);
    return false;
  }
}

export async function generateQR(deviceId, dealId) {
  await ensureWalkerSession(deviceId);
  const body = JSON.stringify({ deviceId, dealId });
  try {
    return await apiFetch('/redemptions/generate-qr', { method: 'POST', body });
  } catch (err) {
    // The session can be missing even after registering — for instance when
    // this device was registered against an older database. Register again and
    // retry once before giving up.
    if (/walker sessie/i.test(err?.message || '')) {
      walkerSessionReady = false;
      await ensureWalkerSession(deviceId);
      return apiFetch('/redemptions/generate-qr', { method: 'POST', body });
    }
    throw err;
  }
}

export async function getQRStatus(qrToken) {
  return apiFetch(`/redemptions/qr-status/${qrToken}`);
}

export async function scanQR(qrToken) {
  return apiFetch('/redemptions/scan', {
    method: 'POST',
    body: JSON.stringify({ qrToken }),
  });
}

export async function scanByCode(code) {
  return apiFetch('/redemptions/scan-code', {
    method: 'POST',
    body: JSON.stringify({ code }),
  });
}

export async function getRecentRedemptions() {
  return apiFetch('/redemptions/recent');
}

// ============================================================================
// WALKER — anonieme device sessie
// ============================================================================

export async function registerWalker(deviceId) {
  return apiFetch('/walkers/register', {
    method: 'POST',
    body: JSON.stringify({ deviceId }),
  });
}

// ============================================================================
// RETAILER — eigen profiel
// ============================================================================

export async function getMyProfile() {
  return apiFetch('/retailers/me');
}

export async function updateMyProfile(payload) {
  return apiFetch('/retailers/profile', {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
}

// POST /deals requires numeric lat/lng. Since 2026-09-06 the backend stores a
// retailer's own coordinates, so source 1 is the normal answer; the rest are
// there for accounts created before that:
//   1. the retailer record from /retailers/me;
//   2. the exact location the retailer picked in their own profile, which is
//      also kept in local storage;
//   3. the centroid of the retailer's area, which always exists — this is the
//      one that used to put every shop in a winkelcentrum on the same point.
// Only when all three fail is there genuinely no location to attach.
function toCoords(lat, lng) {
  const a = Number(lat);
  const b = Number(lng);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
  if (a === 0 && b === 0) return null;
  return { lat: a, lng: b };
}

// The retailer's own exact location, as saved by the profile page. Mirrored to
// the shop record in localStorage so it survives a new tab, since the profile
// itself lives in sessionStorage.
function readLocalShopCoords() {
  try {
    const profile = JSON.parse(sessionStorage.getItem('walkin_shop_profile') || 'null');
    const fromProfile = toCoords(profile?.lat, profile?.lng);
    if (fromProfile) return fromProfile;
  } catch { /* ignore */ }

  const email = (sessionStorage.getItem('walkin_shop_email') || '').toLowerCase();
  if (!email) return null;
  for (const key of ['walkin_shops', 'walkin_admin_shops']) {
    try {
      const list = JSON.parse(localStorage.getItem(key) || '[]');
      const shop = list.find((x) => (x.email || '').toLowerCase() === email);
      const coords = toCoords(shop?.lat, shop?.lng);
      if (coords) return coords;
    } catch { /* ignore */ }
  }
  return null;
}

export async function getRetailerDealCoords() {
  let profile = null;
  try {
    profile = await getMyProfile();
  } catch { /* fall through to the local sources */ }

  const areaId = profile?.areaId || null;

  const fromBackend = toCoords(profile?.lat, profile?.lng);
  if (fromBackend) return { ...fromBackend, source: 'profile', areaId };

  const fromLocal = readLocalShopCoords();
  if (fromLocal) return { ...fromLocal, source: 'local', areaId };

  if (areaId) {
    try {
      const areas = await getAreas();
      const area = Array.isArray(areas) ? areas.find((a) => a.id === areaId) : null;
      const fromArea = toCoords(area?.lat, area?.lng);
      if (fromArea) {
        return { ...fromArea, source: 'area', areaId, areaName: area.name };
      }
    } catch { /* ignore */ }
  }

  return null;
}

// PATCH /retailers/me/tokens/consume — spend from the retailer's own wallet.
//
// Scoped to the JWT, so no id is sent. The backend does the balance check
// inside the UPDATE, so two tabs cannot spend the same token: the loser gets
// a 400 rather than both succeeding.
export async function consumeRetailerTokens(amount) {
  return apiFetch('/retailers/me/tokens/consume', {
    method: 'PATCH',
    body: JSON.stringify({ amount: Number(amount) }),
  });
}

// ============================================================================
// ADMIN — platform beheer (alleen walkin_admin rol)
// ============================================================================

export async function getPlatformStats() {
  return apiFetch('/retailers/admin/stats');
}

export async function getAllRetailers() {
  const rows = await apiFetch('/retailers/admin/all');
  return Array.isArray(rows) ? rows.map(normalizeRetailer) : [];
}

// The admin list plus each shop's token wallet. GET /retailers/admin/all does
// not project the wallet (until backend migration 0.3), so shops without
// counters are completed from their detail route.
export async function getAllRetailersWithWallet() {
  const list = await getAllRetailers();
  const missing = list.filter((r) => r.tokensPurchased === undefined);
  if (missing.length === 0) return list;
  const details = await Promise.allSettled(missing.map((r) => getRetailerById(r.id)));
  const byId = new Map();
  details.forEach((res, i) => {
    if (res.status === 'fulfilled' && res.value) byId.set(missing[i].id, res.value);
  });
  return list.map((r) => {
    const d = byId.get(r.id);
    if (!d) return r;
    return {
      ...r,
      tokensPurchased: d.tokensPurchased,
      tokensUsed: d.tokensUsed,
      tokensAvailable: d.tokensAvailable,
    };
  });
}

// Admin: give (positive) or take (negative) tokens. Adjusts the wallet the way
// a purchase would — `purchased` moves, `used` stays — so the balance changes
// by exactly `delta`. Taking more than is available is clamped to zero.
export async function adminAdjustTokens(retailer, delta, reason) {
  const purchased = Number(retailer?.tokensPurchased) || 0;
  const used = Number(retailer?.tokensUsed) || 0;
  const available = purchased - used;
  const step = Math.max(-available, Math.round(Number(delta) || 0));
  return adminUpdateTokens(retailer.id, { purchased: purchased + step, used, reason });
}

// GET /retailers/admin/:id 404s for ids that GET /retailers/admin/all happily
// returns, which is what produced "Winkelier niet gevonden" on the edit screen
// even though the list rendered fine. Since the list endpoint works, fall back
// to it and match on id or e-mail, so the edit form always opens with the right
// retailer whichever identifier the detail route expects.
//
// The mismatch is logged with both identifiers — read it from the browser
// console to see what the detail route rejected.
export async function getRetailerById(id) {
  try {
    return normalizeRetailer(await apiFetch(`/retailers/admin/${id}`));
  } catch (err) {
    if (err?.status !== 404) throw err;

    console.warn(
      `[WalkIn] GET /retailers/admin/${id} returned 404; ` +
      'falling back to the admin retailer list.'
    );

    const all = await getAllRetailers();
    const wanted = String(id).toLowerCase();
    const found = all.find((r) =>
      String(r.id).toLowerCase() === wanted ||
      String(r.email || '').toLowerCase() === wanted
    );

    if (!found) {
      console.warn(
        `[WalkIn] Retailer ${id} is not in /retailers/admin/all either. ` +
        `Ids returned by the list: ${all.map((r) => r.id).join(', ')}`
      );
      throw err;
    }

    console.warn(
      `[WalkIn] Recovered retailer ${found.id} (${found.email}) from the list. ` +
      'The detail endpoint and the list disagree on the identifier.'
    );
    return found;
  }
}

export async function setRetailerActive(id, active) {
  return apiFetch(`/retailers/admin/${id}/active`, {
    method: 'PATCH',
    body: JSON.stringify({ active }),
  });
}

// Pauses every running deal of one retailer. Used when an admin blocks the
// account: a blocked shop must not have a single deal live a moment longer.
// (The backend migration 0.3 does the same server-side; this keeps the
// prototype correct against a backend that does not yet.)
// Returns { paused, failed }.
export async function pauseAllDealsOfRetailer(retailer) {
  const all = await getAllDealsAdmin();
  const id = retailer?.id;
  const name = (retailer?.shopName || retailer?.name || '').trim().toLowerCase();
  const mine = all.filter((d) =>
    (id && d.retailerId === id) ||
    (!d.retailerId && name && (d.shopName || '').trim().toLowerCase() === name)
  );
  const running = mine.filter(
    (d) => !isRemovedDeal(d) && (d.status === 'actief' || d.status === 'gepland')
  );
  const results = await Promise.allSettled(
    running.map((d) => updateDealStatus(d.id, 'gepauzeerd'))
  );
  const failed = results.filter((r) => r.status === 'rejected').length;
  return { paused: running.length - failed, failed };
}

// Block = account inactive AND all its deals paused, in that order. Unblocking
// only reactivates the account; the deals stay paused until the retailer (or
// an admin) switches them back on, so nothing goes live unreviewed.
export async function setRetailerBlocked(retailer, blocked) {
  await setRetailerActive(retailer.id, !blocked);
  if (!blocked) return { paused: 0, failed: 0 };
  try {
    return await pauseAllDealsOfRetailer(retailer);
  } catch (err) {
    console.warn('[WalkIn] Account blocked, but pausing its deals failed:', err);
    return { paused: 0, failed: -1 };
  }
}

// PATCH /admin/shops/:id/tokens — the retailer's token wallet.
//
// Deliberately separate from adminUpdateShop: the backend keeps a ledger
// rather than a free-form field, so a routine profile save can never move a
// balance by accident.
//
// The backend stores two counters and derives the balance. It rejects
// used > purchased with a 400 rather than letting the database constraint
// turn it into a 500, so the caller gets a message naming the problem.
// Omitting either counter leaves it at its current value.
export async function adminUpdateTokens(id, { purchased, used, reason } = {}) {
  const body = {};
  if (purchased !== undefined && purchased !== null) body.purchased = Number(purchased);
  if (used !== undefined && used !== null) body.used = Number(used);
  // Stored in token_transactions.note (backend 0.3); ignored by older backends.
  if (reason) body.reason = String(reason).slice(0, 200);

  const updated = await apiFetch(`/admin/shops/${id}/tokens`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
  return normalizeRetailer(updated);
}

// PUT /admin/shops/:id — the admin counterpart of PUT /retailers/profile.
// Added 2026-09-19; before it existed the admin could only block or unblock a
// shop (PATCH /retailers/admin/:id/active) and every other edit stayed in the
// browser. Unlike the retailer route this one also works on blocked shops.
//
// Accepts either the admin panel's vocabulary (shopName, area as a name) or
// the backend's (name, areaId). Anything the caller leaves out is not sent, so
// a partial patch cannot blank the rest of the record.
//
// Note: logoUrl is deliberately not accepted — the backend DTO does not carry
// it, and the ValidationPipe would strip it into a 200 that changed nothing.
export async function adminUpdateShop(id, data = {}) {
  const body = {};

  const name = data.name ?? data.shopName;
  if (name !== undefined) body.name = name;

  // The address is trimmed and lowercased here as well as on the server: the
  // backend's @IsEmail() runs before its own normalisation, so a pasted
  // address with a stray space would come back as a 400 instead of saving.
  // The server still lowercases — auth.login() matches case-sensitively, so a
  // mixed-case address would lock the retailer out of their own account.
  if (data.email !== undefined) body.email = String(data.email).trim().toLowerCase();

  for (const key of ['address', 'city', 'postcode', 'description', 'category', 'instructions', 'openingHours']) {
    if (data[key] !== undefined) body[key] = data[key];
  }

  // An unknown areaId makes the backend answer 500, so a gebied passed by name
  // is resolved against /areas first and dropped when it matches nothing.
  let areaId = data.areaId;
  if (areaId === undefined && data.area) areaId = await getAreaIdByName(data.area);
  if (areaId) body.areaId = areaId;

  // lat/lng only mean anything as a pair: two numbers move the shop, two nulls
  // clear its location. Note the null case has to come first — Number(null) is
  // 0, so a "clear" would otherwise be sent as the coordinates of Null Island.
  if (data.lat === null && data.lng === null) {
    body.lat = null;
    body.lng = null;
  } else {
    const coords = toCoords(data.lat, data.lng);
    if (coords) {
      body.lat = coords.lat;
      body.lng = coords.lng;
    }
  }

  const updated = await apiFetch(`/admin/shops/${id}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
  return normalizeRetailer(updated);
}

// ============================================================================
// Device ID — anonieme walker identificatie (vervangt walker UUID generatie)
// ============================================================================

export function getOrCreateDeviceId() {
  let id = localStorage.getItem('walkin_device_id');
  if (!id) {
    id = 'web-' + crypto.randomUUID();
    localStorage.setItem('walkin_device_id', id);
  }
  return id;
}
